import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { IAuthStore } from '../../auth/auth.store';
import { NotificationLevel, ToastNotification } from '../../notifications/models/notification.model';
import { INotificationsService } from '../../notifications/services/notifications.service';
import { IUserStore, ListViewMode } from '../../user/user.store';
import { DatasetDetailsImagesList } from '../components/DatasetDetailsImagesList';
import { DatasetDetailsTabs } from '../components/DatasetDetailsTabs';
import { DatasetDetails } from '../DatasetDetails.context';
import { Datasets, IDatasetsContext } from '../datasets.context';
import { useDatasetPermissions } from '../datasets.permissions';
import { ISelectedImage } from '../datasetsDetails.store';
import { IImagesUploaderService } from '../imagesUpload.service';

export const DatasetDetailsImagesContainer = observer((): ReactElement => {
  const imagesService = useInjection(IImagesUploaderService.$);
  const notificationService = useInjection(INotificationsService.$);
  const authStore = useInjection(IAuthStore.$);
  const userStore = useInjection(IUserStore.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const datasetsPermissions = useDatasetPermissions();

  const { isLoading, withLoaderAsync } = useLoader();
  const datasetDetailsContext = useContext(DatasetDetails);
  const datasetsContext = useContext(Datasets);
  const { datasetId } = useParams<{ datasetId: string }>();

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [imageIdToDeleteWasAnnotated, setImageIdToDeleteWasAnnotated] = useState(false);
  const [imageIdToDeleteIsLocked, setImageIdToDeleteIsLocked] = useState(false);
  const [imageIdToDelete, setImageIdToDelete] = useState<string | undefined>(undefined);
  const [lastSelectedImageId, setLastSelectedImageId] = useState<string | undefined>(undefined);
  const [showRenameImageConfirmationModal, setShowRenameImageConfirmationModal] = useState(false);
  const [renameImageId, setRenameImageId] = useState('');
  const [renameImageName, setRenameImageName] = useState('');
  const [renameImageNameStatus, setRenameImageNameStatus] = useState(InputStatus.empty());

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      const { service } = datasetDetailsContext;

      const isSuccessful = await withLoaderAsync(async (): Promise<boolean> => {
        return await service.getDatasetDetailsImageViewAsync(datasetId);
      });

      if (!isSuccessful) {
        return;
      }

      imagesService.onUploadFinishedSuccessfully.push(refresh);
      imagesService.onUploadAborted.push(refresh);
    };

    void initialize();

    return (): void => {
      let index = imagesService.onUploadFinishedSuccessfully.indexOf(refresh);
      if (index > -1) {
        imagesService.onUploadFinishedSuccessfully.splice(index, 1);
      }
      index = imagesService.onUploadAborted.indexOf(refresh);
      if (index > -1) {
        imagesService.onUploadAborted.splice(index, 1);
      }
    };
  }, []);

  const refresh = async (): Promise<void> => {
    const paging = datasetDetailsContext.service.store.detailsImagesPaging;
    await datasetDetailsContext.service.refreshImages(datasetId, paging.pageNumber, paging.pageSize, paging.orderBy, paging.orderType);
  };

  const onPaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    const paging = datasetDetailsContext.service.store.detailsImagesPaging;
    await withLoaderAsync(async () => {
      await datasetDetailsContext.service.getDatasetImagesAsync(datasetId, pageNumber, pageSize, paging.orderBy, paging.orderType);
    });
  };

  const handleOrderingChange = async (orderBy: string, orderType: string): Promise<void> => {
    const paging = datasetDetailsContext.service.store.detailsImagesPaging;
    await withLoaderAsync(async () => {
      await datasetDetailsContext.service.getDatasetImagesAsync(
        datasetDetailsContext.service.store.details.id,
        paging.pageNumber,
        paging.pageSize,
        orderBy,
        orderType
      );
    });
  };

  const handleDownloadImage = async (id: string): Promise<void> => {
    const result = await datasetDetailsContext.service.downloadImageAsync(id);
    if (result instanceof ACDLError) {
      await notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'cant_download_image'));
    }
  };

  const handleDeleteImage = (id: string): void => {
    const image = datasetDetailsContext.service.store.detailsImages.find((x) => x.id === id);

    setShowDeleteConfirmationModal(true);
    setImageIdToDelete(id);
    setImageIdToDeleteWasAnnotated(image !== undefined && image.hasAnnotations);
    setImageIdToDeleteIsLocked(image !== undefined && image.hasLocks);
  };

  const handleCancelDeleteImage = (): void => {
    setShowDeleteConfirmationModal(false);
    setImageIdToDelete(undefined);
    setImageIdToDeleteWasAnnotated(false);
    setImageIdToDeleteIsLocked(false);
  };

  const handleConfirmDeleteImage = (datasets: IDatasetsContext) => async (): Promise<void> => {
    if (imageIdToDelete) {
      await datasetDetailsContext.service.deleteImageAsync(imageIdToDelete);
    }
    setShowDeleteConfirmationModal(false);
    setImageIdToDelete(undefined);
    const paging = datasetDetailsContext.store.detailsImagesPaging;
    await withLoaderAsync(async () => {
      await datasetDetailsContext.service.getDatasetImagesAsync(
        datasetDetailsContext.store.details.id,
        paging.pageNumber,
        paging.pageSize,
        paging.orderBy,
        paging.orderType
      );
    });
    datasets.store.refreshFromDetails(datasetDetailsContext.store.details);
  };

  const toggleRenameImageModal = (imageId: string): void => {
    const image = datasetDetailsContext.store.detailsImages.find((i) => i.id === imageId);

    setShowRenameImageConfirmationModal((prevState) => !prevState);
    setRenameImageId(imageId);
    setRenameImageName(image ? image.name : '');
    setRenameImageNameStatus(InputStatus.empty());
  };

  const handleRenameImageNameChange = (value: string): void => {
    setRenameImageName(value);
    void validateNameDebounced(value);
  };

  const validateName = async (name: string): Promise<InputStatus> => {
    const inputStatus = await datasetDetailsContext.service.validateImageNameAsync(name, renameImageId, datasetId);
    setRenameImageNameStatus(inputStatus);
    return inputStatus;
  };

  const validateNameDebounced = useDebouncedCallback(async (name: string): Promise<void> => {
    await validateName(name);
  }, 300);

  const renameImage = async (): Promise<void> => {
    const status = await validateName(renameImageName);
    if (status.isValid !== true) {
      return;
    }

    const result = await datasetDetailsContext.service.renameImageAsync(renameImageId, renameImageName, datasetId);

    if (result instanceof ACDLError) {
      setRenameImageNameStatus(InputStatus.buildFrom(result.apiErrorResponse?.errorCodes ?? []));
    } else {
      setRenameImageNameStatus(InputStatus.empty());
      setShowRenameImageConfirmationModal(false);
    }
  };

  const handleNonBatchCheck = (id: string): void => {
    setLastSelectedImageId(id);

    const store = datasetDetailsContext.store;
    const imageToAdd = store.detailsImages.filter((i) => i.id === id)[0];

    const existing = store.selectedImages.find((si) => si.id === imageToAdd.id);
    if (existing) {
      store.selectedImages.remove(existing);
    } else {
      store.selectedImages.push({ id: imageToAdd.id, hasLock: imageToAdd.hasLocks, hasAnnotation: imageToAdd.hasAnnotations });
    }
  };

  const handleBatchCheck = (id: string): void => {
    const store = datasetDetailsContext.store;

    const lastIndex = store.detailsImages.findIndex((i) => i.id === lastSelectedImageId);
    const markUpToIndex = store.detailsImages.findIndex((i) => i.id === id);

    if (lastIndex === undefined || markUpToIndex === undefined || lastIndex === -1 || markUpToIndex === -1) {
      handleNonBatchCheck(id);
      return;
    }

    const changeDirection = lastIndex < markUpToIndex ? 1 : -1;
    const comparison = lastIndex < markUpToIndex ? (k: number, n: number): boolean => k <= n : (k: number, n: number): boolean => k >= n;

    for (let i = lastIndex; comparison(i, markUpToIndex); i += changeDirection) {
      const image = store.detailsImages[i];
      const imageToAdd: ISelectedImage = { id: image.id, hasAnnotation: image.hasAnnotations, hasLock: image.hasLocks };
      const canDelete = datasetsPermissions.canDeleteMarkedImages().allowed || (!image.hasAnnotations && !image.hasLocks);

      if (!store.selectedImages.find((image) => image.id === imageToAdd.id) && canDelete) {
        store.selectedImages.push(imageToAdd);
      }
    }
  };

  const handleSelectAll = (isSelected: boolean): void => {
    datasetDetailsContext.store.selectedImages.clear();
    if (isSelected) {
      const images = datasetDetailsContext.store.detailsImages.map((image) => ({ id: image.id, hasAnnotation: image.hasAnnotations, hasLock: image.hasLocks }));
      datasetDetailsContext.store.selectedImages.push(...images);
    }
  };

  const handleThumbCheck = (id: string, isBatch: boolean): void => {
    if (isBatch && lastSelectedImageId) {
      handleBatchCheck(id);
    } else {
      handleNonBatchCheck(id);
    }
  };

  const imageClickCallback = (): void => {
    datasetDetailsContext.service.setDefaultImagesPaging();
  };

  const workspace = currentWorkspaceStore.currentWorkspace;
  const details = datasetDetailsContext.store.details;
  const store = datasetDetailsContext.store;

  return (
    <ColumnLayout size='none'>
      <DatasetDetailsTabs
        workspaceId={workspace.id}
        details={details}
        imageClickCallback={imageClickCallback}
      />
      <DatasetDetailsImagesList
        datasetId={datasetId}
        workspaceId={workspace.id}
        datasetImages={store.detailsImages || []}
        isLoading={isLoading}
        paging={store.detailsImagesPaging}
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        isImageDownloadable={store.details.canDownload || false}
        disabled={workspace.locked}
        onImageDownload={handleDownloadImage}
        onImageDeleted={handleDeleteImage}
        onCancelImageDelete={handleCancelDeleteImage}
        onConfirmImageDelete={handleConfirmDeleteImage(datasetsContext)}
        onPaginationChange={onPaginationChange}
        onOrderingChange={handleOrderingChange}
        viewMode={userStore.datasetImageListViewMode || ListViewMode.List}
        authToken={authStore.token}
        imageIdToDeleteWasAnnotated={imageIdToDeleteWasAnnotated || false}
        imageIdToDeleteIsLocked={imageIdToDeleteIsLocked || false}
        isUploading={store.details.status === 'Uploading' || store.details.status === 'PublishedUploading'}
        imagesToBulkDelete={store.selectedImages.map((si) => si.id) || []}
        onThumbCheck={handleThumbCheck}
        onSelectAll={handleSelectAll}
        showRenameImageConfirmationModal={showRenameImageConfirmationModal}
        renameImageName={renameImageName}
        handleRenameImageNameChange={handleRenameImageNameChange}
        renameImage={renameImage}
        renameImageInputStatus={renameImageNameStatus}
        toggleRenameImageModal={toggleRenameImageModal}
        onRenameImage={toggleRenameImageModal}
      />
    </ColumnLayout>
  );
});
